/**
 * Video Showcase
 * - Adds an accessible pause/play toggle for the auto-playing featured video.
 * - Supports native <video>, YouTube iframes, and Vimeo iframes.
 * - Honors prefers-reduced-motion (starts paused).
 * - Pauses video when offscreen for performance and battery.
 *
 * WCAG 2.2 references:
 *  - 2.2.2 Pause, Stop, Hide (Level A): user can pause auto-playing motion.
 *  - 4.1.2 Name, Role, Value: button uses aria-pressed + dynamic aria-label.
 *  - 2.1.1 Keyboard: native <button>, fully keyboard operable.
 *  - 2.4.7 Focus Visible: high-contrast focus ring in CSS.
 */
if (!customElements.get('video-showcase-component')) {
  customElements.define(
    'video-showcase-component',
    class VideoShowcase extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
        this.swipeMobile = this.dataset.swipeMobile === 'true';
        // Mobile swipe is handled via CSS — no JS needed here.
      }
    }
  );
}

/**
 * Pause toggle controller — one instance per featured video on the page.
 */
class VideoShowcasePauseToggle {
  constructor(button) {
    this.button = button;
    this.statusEl = button.querySelector('[data-video-pause-toggle-status]');
    this.featured = button.closest('.video-showcase__featured-video');
    if (!this.featured) return;

    this.labelPause = button.dataset.labelPause || 'Pause background video';
    this.labelPlay = button.dataset.labelPlay || 'Play background video';

    // The host theme's <video-element> swaps the <template> contents into
    // the live DOM on activation. We watch for that mutation rather than
    // polling, so the click handler always has a real target.
    this.videoElementHost = this.featured.querySelector('video-element');
    this.mediaType = null;     // 'native' | 'youtube' | 'vimeo'
    this.mediaEl = null;
    this.youtubeReady = false; // YouTube needs a listening handshake first

    this.isPlaying = true;
    this.userInitiatedPause = false;

    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleFrameMessage = this.handleFrameMessage.bind(this);

    this.init();
  }

  init() {
    // Bind interaction handlers FIRST — even if media isn't resolved yet,
    // the button still reacts; we'll queue the action until media exists.
    this.button.addEventListener('click', this.handleClick);
    // Belt-and-braces: native <button> handles Enter/Space already, but if
    // anything in the theme's CSS were to change pointer-events on a parent
    // and break click delegation, this keeps keyboard control alive.
    this.button.addEventListener('keydown', this.handleKeydown);

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('message', this.handleFrameMessage);

    // Try once now in case it's already there.
    this.resolveMediaElement();

    // Watch for the template swap. <video-element> moves children from a
    // <template> into itself; that's a subtree mutation we can react to
    // immediately (no polling, no setTimeout chain).
    if (this.videoElementHost && !this.mediaEl) {
      this.mutationObserver = new MutationObserver(() => {
        if (this.mediaEl) return;
        this.resolveMediaElement();
        if (this.mediaEl && this.mutationObserver) {
          this.mutationObserver.disconnect();
          this.mutationObserver = null;
        }
      });
      this.mutationObserver.observe(this.videoElementHost, {
        childList: true,
        subtree: true,
      });
    }

    // Pause when offscreen — saves CPU/bandwidth on long pages.
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              if (!this.userInitiatedPause && !this.prefersReducedMotion) {
                this.play({ silent: true });
              }
            } else {
              this.pause({ silent: true });
            }
          }
        },
        { threshold: 0.1 }
      );
      this.observer.observe(this.featured);
    }

    // prefers-reduced-motion users meet the section paused.
    if (this.prefersReducedMotion) {
      this.userInitiatedPause = true; // treat as user intent so we don't auto-resume
      this.pause({ silent: true });
      this.setButtonState(false);
    } else {
      this.setButtonState(true);
    }
  }

  resolveMediaElement() {
    if (!this.videoElementHost || this.mediaEl) return;

    const native = this.videoElementHost.querySelector('video');
    if (native) {
      this.mediaType = 'native';
      this.mediaEl = native;
      native.addEventListener('play', () => this.setButtonState(true));
      native.addEventListener('pause', () => this.setButtonState(false));
      this.isPlaying = !native.paused;
      this.setButtonState(this.isPlaying, { silent: true });
      this.flushPendingAction();
      return;
    }

    const iframe = this.videoElementHost.querySelector('iframe');
    if (iframe) {
      const src = iframe.src || '';
      if (src.includes('youtube.com') || src.includes('youtu.be')) {
        this.mediaType = 'youtube';
        // YouTube requires a 'listening' handshake before commands take effect.
        // We send it once the iframe has loaded — and again on a short timer,
        // because some browsers fire `load` before YT's internal bridge is up.
        const handshake = () => {
          this.postToFrame('{"event":"listening","id":1,"channel":"widget"}');
        };
        if (iframe.contentWindow) handshake();
        iframe.addEventListener('load', () => {
          handshake();
          // Mark ready slightly after the handshake to allow a queued action.
          setTimeout(() => {
            this.youtubeReady = true;
            this.flushPendingAction();
          }, 250);
        });
        // If the iframe was already loaded (cached), assume readiness shortly.
        setTimeout(() => {
          this.youtubeReady = true;
          this.flushPendingAction();
        }, 800);
      } else if (src.includes('vimeo.com')) {
        this.mediaType = 'vimeo';
      }
      this.mediaEl = iframe;
      this.flushPendingAction();
    }
  }

  // If the user clicked before media was ready, we stash the intent and run it
  // as soon as we can — so the button never feels dead.
  flushPendingAction() {
    if (!this.pendingAction || !this.mediaEl) return;
    if (this.mediaType === 'youtube' && !this.youtubeReady) return;
    const action = this.pendingAction;
    this.pendingAction = null;
    if (action === 'play') this.play();
    else if (action === 'pause') this.pause();
  }

  handleClick(event) {
    event.preventDefault();
    this.toggle();
  }

  handleKeydown(event) {
    // Native <button> handles Enter/Space, but we make the contract explicit.
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this.toggle();
    }
  }

  toggle() {
    if (this.isPlaying) {
      this.userInitiatedPause = true;
      this.pause();
    } else {
      this.userInitiatedPause = false;
      this.play();
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.pause({ silent: true });
    } else if (!this.userInitiatedPause && !this.prefersReducedMotion) {
      this.play({ silent: true });
    }
  }

  handleFrameMessage(event) {
    // We don't need to act on YouTube/Vimeo replies for this control yet,
    // but the listener exists so future event hooks are trivial to add.
    if (this.mediaType === 'youtube' && event.source === this.mediaEl?.contentWindow) {
      // YouTube responses confirm bridge is up.
      this.youtubeReady = true;
    }
  }

  play({ silent = false } = {}) {
    if (!this.mediaEl) {
      this.pendingAction = 'play';
      this.setButtonState(true, { silent });
      return;
    }

    if (this.mediaType === 'native') {
      const playPromise = this.mediaEl.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => this.setButtonState(false));
      }
    } else if (this.mediaType === 'youtube') {
      if (!this.youtubeReady) {
        this.pendingAction = 'play';
      } else {
        this.postToFrame('{"event":"command","func":"playVideo","args":""}');
      }
    } else if (this.mediaType === 'vimeo') {
      this.postToFrame(JSON.stringify({ method: 'play' }));
    }

    this.setButtonState(true, { silent });
  }

  pause({ silent = false } = {}) {
    if (!this.mediaEl) {
      this.pendingAction = 'pause';
      this.setButtonState(false, { silent });
      return;
    }

    if (this.mediaType === 'native') {
      this.mediaEl.pause();
    } else if (this.mediaType === 'youtube') {
      if (!this.youtubeReady) {
        this.pendingAction = 'pause';
      } else {
        this.postToFrame('{"event":"command","func":"pauseVideo","args":""}');
      }
    } else if (this.mediaType === 'vimeo') {
      this.postToFrame(JSON.stringify({ method: 'pause' }));
    }

    this.setButtonState(false, { silent });
  }

  postToFrame(message) {
    if (!this.mediaEl || !this.mediaEl.contentWindow) return;
    try {
      this.mediaEl.contentWindow.postMessage(message, '*');
    } catch (_) {
      /* cross-origin frame not ready */
    }
  }

  /**
   * - aria-pressed reflects "is the toggle engaged" → true when paused.
   * - aria-label updates so the button announces what it WILL do.
   * - polite live region announces what just HAPPENED.
   */
  setButtonState(isPlaying, { silent = false } = {}) {
    this.isPlaying = isPlaying;
    this.button.setAttribute('aria-pressed', String(!isPlaying));
    this.button.setAttribute('aria-label', isPlaying ? this.labelPause : this.labelPlay);
    this.button.dataset.state = isPlaying ? 'playing' : 'paused';

    if (!silent && this.statusEl) {
      this.statusEl.textContent = isPlaying ? 'Video playing' : 'Video paused';
    }
  }

  destroy() {
    this.button.removeEventListener('click', this.handleClick);
    this.button.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('message', this.handleFrameMessage);
    if (this.observer) this.observer.disconnect();
    if (this.mutationObserver) this.mutationObserver.disconnect();
  }
}

function initVideoShowcasePauseToggles(scope = document) {
  scope.querySelectorAll('[data-video-pause-toggle]').forEach((btn) => {
    if (btn.__pauseToggleInstance) return;
    btn.__pauseToggleInstance = new VideoShowcasePauseToggle(btn);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initVideoShowcasePauseToggles());
} else {
  initVideoShowcasePauseToggles();
}

document.addEventListener('shopify:section:load', (event) => {
  initVideoShowcasePauseToggles(event.target);
});
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-video-pause-toggle]').forEach((btn) => {
    if (btn.__pauseToggleInstance) {
      btn.__pauseToggleInstance.destroy();
      delete btn.__pauseToggleInstance;
    }
  });
});
